import { flexRender, type HeaderGroup } from '@tanstack/react-table';

import type { OrderDirection } from '@repo/frontend-shared';
import { SDSCheckbox } from '@repo/sds/components/SDSCheckbox';
import { SDSTableHeader } from '@repo/sds/components/SDSTable/SDSTableHeader';
import { SDSTableRow } from '@repo/sds/components/SDSTable/SDSTableRow';
import type { SDSTableFeatures, TableData } from '@repo/sds/components/SDSTable/types/table.types';
import { getColumnAlign, getColumnWidth, getHeaderDataTestId } from '@repo/sds/components/SDSTable/utils/header-utils';
import { tanStackSortToOrderDirection } from '@repo/sds/components/SDSTable/utils/sort-utils';

function resolveHeaderSortDirection(
  isExternalSorting: boolean,
  isActiveExternalSortField: boolean,
  externalSortDirection: OrderDirection | undefined,
  columnIsSorted: false | 'asc' | 'desc',
): OrderDirection | false {
  if (isExternalSorting && isActiveExternalSortField && externalSortDirection) {
    return externalSortDirection;
  }
  return tanStackSortToOrderDirection(columnIsSorted);
}

interface SDSTableHeaderRowProps<T extends TableData> {
  headerGroup: HeaderGroup<SDSTableFeatures, T>;
  isExternalSorting: boolean;
  sortField: string | undefined;
  sortDirection: OrderDirection | undefined;
  onSort: ((field: string) => void) | undefined;
  allSelected: boolean;
  onSelectAll: (checked: boolean) => void;
}

export function SDSTableHeaderRow<T extends TableData>({
  headerGroup,
  isExternalSorting,
  sortField,
  sortDirection,
  onSort,
  allSelected,
  onSelectAll,
}: SDSTableHeaderRowProps<T>) {
  return (
    <SDSTableRow isHeader>
      {headerGroup.headers.map(header => {
        const sortDir = resolveHeaderSortDirection(
          isExternalSorting,
          header.column.id === sortField,
          sortDirection,
          header.column.getIsSorted(),
        );
        const handleHeaderClick = () => {
          if (isExternalSorting && onSort && header.column.id !== '_selection') {
            onSort(header.column.id);
          } else if (!isExternalSorting) {
            header.column.toggleSorting();
          }
        };

        return (
          <SDSTableHeader
            key={header.id}
            sortable={header.column.getCanSort()}
            sortDirection={sortDir}
            align={getColumnAlign(header.column.columnDef.meta)}
            style={{ width: getColumnWidth(header.column.columnDef.meta) }}
            data-testid={getHeaderDataTestId(header.column.id, header.column.columnDef.meta)}
            onClick={handleHeaderClick}
          >
            {header.column.id === '_selection' ? (
              <div className="SDSTable__selection-cell">
                <SDSCheckbox
                  isChecked={allSelected}
                  onChange={onSelectAll}
                  ariaLabel="Select all rows"
                  mode="active"
                  className="SDSTable__checkbox"
                />
              </div>
            ) : (
              flexRender(header.column.columnDef.header, header.getContext())
            )}
          </SDSTableHeader>
        );
      })}
    </SDSTableRow>
  );
}
